import React, { useEffect, useMemo, useState } from "react";
import MaterialTable from "material-table";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { ThemeProvider } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { normalizeDate } from "../../../utilities";
import { getWindowForms } from "../../../components/Forms/api";
import { selectUser } from "../../../features/userSlice";
import { StatusBadge } from "./WindowFormShared";
import { useAppMuiTheme } from "../../../theme/MuiThemeBridge";
import { icons } from "../../../components/Utilities/UtilitiesWD";
import {
  PageHeader,
  PageShell,
  SectionHeader,
} from "../../../components/UI/Page";
import {
  EmptyState,
  LoadingState,
  StatTile,
} from "../../../components/UI/States";
import {
  FilterChip,
  Pill,
  mtComponents,
  mtHeaderStyle,
  mtRowStyle,
  mtSearchFieldStyle,
  mtStyle,
  waitTone,
} from "../../../components/WindowDoor/wdUi";

const ALL_STATUSES = [
  "Submitted",
  "Under Review",
  "Awaiting Sales Approval",
  "Needs Revision",
  "Reviewing Revisions",
  "Approved",
  "Converted to Order",
  "Cancelled",
];

const statusOf = (entry) => entry?.status || "Submitted";

const daysSince = (date) =>
  Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 86400000));

// Personal RFQ dashboard shared by the quoter (staff) and salesperson
// views. The config decides which statuses are "in my court" and what the
// reader should do about each one.
const WindowFormsMyDashboard = ({
  heading,
  subtitle,
  fetchMode, // "sales" | "assigned"
  detailBase,
  actionStatuses,
  waitingStatuses,
  doneStatuses,
  attentionHints,
  waitingLabel,
  extraColumn,
  emptyMessage,
}) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const defaultMaterialTheme = useAppMuiTheme();
  const [dataStore, setDataStore] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    if (!user?.userID) {
      return;
    }
    const setter = (data) => {
      setDataStore(Array.isArray(data) ? data : []);
      setLoaded(true);
    };
    if (fetchMode === "sales") {
      getWindowForms(setter, user.userID);
    } else {
      getWindowForms(setter, null, user.userID);
    }
  }, [user, fetchMode]);

  const entries = useMemo(
    () => (Array.isArray(dataStore) ? dataStore : []),
    [dataStore]
  );

  const statusCounts = useMemo(() => {
    const counts = {};
    entries.forEach((entry) => {
      const status = statusOf(entry);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [entries]);

  const countFor = (statuses) =>
    statuses.reduce((sum, status) => sum + (statusCounts[status] || 0), 0);

  const actionCount = countFor(actionStatuses);
  const waitingCount = countFor(waitingStatuses);
  const doneCount = countFor(doneStatuses);
  const activeStatuses = [...actionStatuses, ...waitingStatuses];

  // "Waiting since" prefers the last status change so an old RFQ that just
  // came back to this person doesn't look overdue.
  const waitingSince = (entry) =>
    entry?.status_updated_at || entry?.entry_date;

  const attentionList = useMemo(
    () =>
      entries
        .filter((entry) => actionStatuses.includes(statusOf(entry)))
        .sort(
          (a, b) => new Date(waitingSince(a)) - new Date(waitingSince(b))
        ),
    [entries, actionStatuses]
  );

  const filterOptions = [
    { key: "active", label: "All Active", count: countFor(activeStatuses) },
    ...ALL_STATUSES.map((status) => ({
      key: status,
      label: status,
      count: statusCounts[status] || 0,
    })),
    { key: "everything", label: "Everything", count: entries.length },
  ];

  const tableData = useMemo(() => {
    return entries
      .filter((entry) => {
        const status = statusOf(entry);
        if (statusFilter === "active") return activeStatuses.includes(status);
        if (statusFilter === "everything") return true;
        return status === statusFilter;
      })
      .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, statusFilter]);

  const kpiTiles = [
    {
      label: "Needs my action",
      value: actionCount,
      tone: actionCount > 0 ? "sky" : "default",
      icon: PendingActionsOutlinedIcon,
    },
    { label: waitingLabel, value: waitingCount, icon: HourglassEmptyIcon },
    {
      label: "Completed",
      value: doneCount,
      tone: "emerald",
      icon: TaskAltOutlinedIcon,
    },
  ];

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={fetchMode === "sales" ? RequestQuoteOutlinedIcon : AssignmentIndOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title={heading}
        description={subtitle}
      />

      <div className="mb-8 grid grid-cols-3 gap-3">
        {kpiTiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={loaded ? tile.value : "—"}
            tone={tile.tone}
            icon={tile.icon}
          />
        ))}
      </div>

      {!loaded ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading your RFQs…" />
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <SectionHeader title="Needs my attention" />
            {attentionList.length === 0 ? (
              <div className="app-card rounded-2xl border">
                <EmptyState
                  icon={TaskAltOutlinedIcon}
                  title="You're all caught up"
                  description={
                    entries.length === 0
                      ? emptyMessage
                      : "Nothing is waiting on you right now."
                  }
                />
              </div>
            ) : (
              <ul className="app-card divide-y divide-[color:var(--app-divider)] overflow-hidden rounded-2xl border">
                {attentionList.map((entry) => {
                  const status = statusOf(entry);
                  const waitDays = daysSince(waitingSince(entry));
                  return (
                    <li
                      key={entry._id}
                      className="flex flex-col gap-3 px-5 py-4 transition hover:bg-[color:var(--app-hover)] sm:flex-row sm:items-center"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">
                            {entry?.customer || "Unknown customer"}
                          </span>
                          <StatusBadge status={status} />
                          <Pill tone={waitTone(waitDays)}>
                            {waitDays} day{waitDays === 1 ? "" : "s"}
                          </Pill>
                        </div>
                        <p className="app-muted mt-0.5 truncate text-sm">
                          {[
                            entry?.form_name || "Window Form",
                            entry?.account,
                            entry?.job,
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </p>
                        {attentionHints[status] && (
                          <p className="app-accent mt-1 text-sm font-medium">
                            {attentionHints[status]}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="app-btn app-btn-primary self-start sm:self-auto"
                        onClick={() => navigate(`${detailBase}/${entry._id}`)}
                      >
                        Open
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <SectionHeader title="All my RFQs" />
            <div className="mb-3 flex flex-wrap gap-2">
              {filterOptions.map((option) => (
                <FilterChip
                  key={option.key}
                  active={statusFilter === option.key}
                  onClick={() => setStatusFilter(option.key)}
                  count={option.count}
                >
                  {option.label}
                </FilterChip>
              ))}
            </div>
            <div className="app-card overflow-hidden rounded-2xl border">
              <ThemeProvider theme={defaultMaterialTheme}>
                <MaterialTable
                  components={mtComponents}
                  columns={[
                    {
                      title: "Received",
                      field: "entry_date",
                      defaultSort: "desc",
                      customSort: (a, b) =>
                        new Date(a.entry_date) - new Date(b.entry_date),
                      render: (rowData) => (
                        <span className="whitespace-nowrap tabular-nums">
                          {normalizeDate(new Date(rowData?.entry_date))}
                        </span>
                      ),
                    },
                    {
                      title: "Customer / Job",
                      field: "customer",
                      render: (rowData) => (
                        <div className="flex flex-col">
                          <span className="font-semibold">
                            {rowData?.customer || "Unknown customer"}
                          </span>
                          {(rowData?.account || rowData?.job) && (
                            <span className="app-muted text-xs">
                              {[rowData?.account, rowData?.job]
                                .filter(Boolean)
                                .join(" • ")}
                            </span>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: "Form",
                      field: "form_name",
                      render: (rowData) => rowData?.form_name || "Window Form",
                    },
                    ...(extraColumn ? [extraColumn] : []),
                    {
                      title: "Status",
                      field: "status",
                      render: (rowData) => (
                        <StatusBadge status={statusOf(rowData)} />
                      ),
                    },
                    {
                      title: "",
                      sorting: false,
                      render: (rowData) => (
                        <button
                          type="button"
                          className="app-btn app-btn-secondary app-btn-sm"
                          onClick={() =>
                            navigate(`${detailBase}/${rowData?._id}`)
                          }
                        >
                          View
                        </button>
                      ),
                    },
                  ]}
                  data={tableData}
                  title="Submissions"
                  icons={icons}
                  style={mtStyle}
                  options={{
                    pageSize: 10,
                    emptyRowsWhenPaging: false,
                    pageSizeOptions: [5, 10, 15, 25],
                    headerStyle: mtHeaderStyle,
                    searchFieldStyle: mtSearchFieldStyle,
                    rowStyle: mtRowStyle,
                  }}
                  localization={{
                    body: {
                      emptyDataSourceMessage: (
                        <EmptyState
                          compact
                          icon={SearchOffIcon}
                          title="No RFQs"
                          description="Nothing matches the current filter."
                        />
                      ),
                    },
                  }}
                />
              </ThemeProvider>
            </div>
          </section>
        </div>
      )}
    </PageShell>
  );
};

export default WindowFormsMyDashboard;
